import { useState, useEffect, useCallback } from 'react';
import { RootState, useAppSelector } from '../../../../state/state.tsx';
import { selectActiveRole, selectEffectiveUID } from "../../../../state/UserStatesV2/userSliceV2.ts";
import { collection, getDocs, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../../Config/firebase';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    Button, 
    TextField, 
    IconButton,
    Chip,
    Typography,
    Card,
    CardContent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { toast } from 'react-toastify';

export default function MyWorld() {
    const ServiceProviderID = useAppSelector(selectEffectiveUID);
    const activeRole = useAppSelector(selectActiveRole);
    const userData = useAppSelector((state: RootState) => state.userSliceV2.data);

    // Check-in modal state (exact same as MySubscriptions)
    const [showCheckInModal, setShowCheckInModal] = useState(false);
    const [availableSubscriptions, setAvailableSubscriptions] = useState<any[]>([]);
    const [subscriptionsMap, setSubscriptionsMap] = useState<Record<string, string>>({});
    const [acceptedRoles, setAcceptedRoles] = useState<any[]>([]);
    const [isEditingExistingEntry, setIsEditingExistingEntry] = useState(false);
    const [currentEntryStatus, setCurrentEntryStatus] = useState<string | null>(null);
    const [attendanceFormData, setAttendanceFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        subscriptions: [] as any[],
        totalHours: 0
    });

    // Calendar state (reusing TeamManagement logic)
    const [attendanceYear, setAttendanceYear] = useState(new Date().getFullYear());
    const [attendanceMonth, setAttendanceMonth] = useState(new Date().getMonth());
    const [myAttendance, setMyAttendance] = useState([]);
    const [attendanceLoading, setAttendanceLoading] = useState(false);
    const [lockedMonths, setLockedMonths] = useState<{[key: string]: boolean}>({});

    // Effects from MySubscriptions to load subscriptions and roles
    useEffect(() => {
        const fetchSubscriptions = async () => {
            if (!ServiceProviderID) return;
            try {
                const subscriptionsRef = collection(db, `ServiceProviders/${ServiceProviderID}/Subscriptions`);
                const subscriptionsSnap = await getDocs(subscriptionsRef);
                
                // Create subscriptionsMap (for attendance entry compatibility)
                const subscriptionsMapData: Record<string, string> = {};
                subscriptionsSnap.docs.forEach((doc) => {
                    const data = doc.data();
                    const subscriptionName = data.Name || data.name || data.title || data.subscriptionName || data.planName || data.displayName || `Subscription ${doc.id}`;
                    subscriptionsMapData[doc.id] = subscriptionName;
                });
                setSubscriptionsMap(subscriptionsMapData);
                console.log('🔍 MyWorld - Loaded subscriptions:', {
                    serviceProviderID: ServiceProviderID,
                    subscriptionsCount: subscriptionsMapData.length,
                    subscriptionIds: Object.keys(subscriptionsMapData)
                });
            } catch (error) {
                console.error('Error fetching subscriptions:', error);
            }
        };
        fetchSubscriptions();
    }, [ServiceProviderID]);

    // Effect to fetch delegated roles (matching MySubscriptions implementation)
    useEffect(() => {
        const fetchDelegatedRoles = async () => {
            if (!ServiceProviderID) return;
            try {
                const serviceProviderRef = doc(db, 'ServiceProviders', ServiceProviderID);
                const serviceProviderDoc = await getDoc(serviceProviderRef);
                
                if (serviceProviderDoc.exists()) {
                    const data = serviceProviderDoc.data();
                    const delegatedRoles = data.delegatedRoles || [];
                    const acceptedRolesList = delegatedRoles.filter((role: any) => role.status === 'accepted');
                    setAcceptedRoles(acceptedRolesList);
                }
            } catch (error) {
                console.error('Error fetching delegated roles:', error);
            }
        };
        fetchDelegatedRoles();
    }, [ServiceProviderID]);

    // Get subscriptions for attendance
    const getSubscriptionsForAttendanceEntry = useCallback(() => {
        const isOwner = !activeRole || activeRole?.role === null;
        
        // Check if current user is Administrator (from activeRole)
        const isAdministrator = activeRole?.role === 'Administrator';
        
        // Also check if user is in the acceptedRoles (for backwards compatibility)
        const currentUserRole = acceptedRoles.find(role => role.number === userData?.phone_number);
        const isAdminInAcceptedRoles = currentUserRole?.role === 'Administrator';
        
        if (isOwner || isAdministrator || isAdminInAcceptedRoles) {
            // Owner and Admin roles can log attendance across all subscriptions
            const allSubs = Object.entries(subscriptionsMap || {}).map(([id, name]) => ({
                id,
                Name: name,
                Description: ''
            }));
            console.log('🔍 MyWorld - User has full subscription access:', {
                isOwner,
                isAdministrator,
                isAdminInAcceptedRoles,
                activeRole: activeRole?.role,
                subscriptionsCount: allSubs.length,
                serviceProviderID: ServiceProviderID
            });
            return allSubs;
        } else {
            // Check for assigned subscriptions from either acceptedRoles or activeRole
            let availableSubIds: string[] = [];
            
            // First check acceptedRoles (for delegated roles)
            if (currentUserRole?.assignedSubscriptions) {
                availableSubIds = currentUserRole.assignedSubscriptions.map((ref: any) => {
                    const pathParts = ref.path.split('/');
                    return pathParts[pathParts.length - 1];
                });
            }
            
            // Also check activeRole.assignedSubscriptions (for Manager/Observer roles)
            if (activeRole?.assignedSubscriptions && availableSubIds.length === 0) {
                availableSubIds = activeRole.assignedSubscriptions.map((ref: any) => {
                    // Handle both DocumentReference objects and string paths
                    if (typeof ref === 'string') {
                        return ref;
                    } else if (ref.path) {
                        const pathParts = ref.path.split('/');
                        return pathParts[pathParts.length - 1];
                    }
                    return ref;
                });
            }
            
            if (availableSubIds.length > 0) {
                // All other roles (Manager, Observer, Staff) can log for their assigned subscriptions
                const filteredSubs = Object.entries(subscriptionsMap || {})
                    .filter(([id]) => availableSubIds.includes(id))
                    .map(([id, name]) => ({
                        id,
                        Name: name,
                        Description: ''
                    }));
                
                console.log('🔍 MyWorld - User has assigned subscription access:', {
                    role: activeRole?.role,
                    availableSubIds,
                    filteredSubsCount: filteredSubs.length,
                    subscriptionsMapCount: Object.keys(subscriptionsMap || {}).length
                });
                return filteredSubs;
            }
            
            // No assigned subscriptions found
            console.log('🔍 MyWorld - No assigned subscriptions found:', {
                role: activeRole?.role,
                hasActiveRoleAssignedSubs: !!activeRole?.assignedSubscriptions,
                hasCurrentUserRoleAssignedSubs: !!currentUserRole?.assignedSubscriptions
            });
            return [];
        }
    }, [activeRole, acceptedRoles, userData?.phone_number, subscriptionsMap, ServiceProviderID]);

    // Load user's assigned subscriptions for attendance (exact same as MySubscriptions)
    const loadUserSubscriptions = useCallback(() => {
        const availableSubs = getSubscriptionsForAttendanceEntry();
        setAvailableSubscriptions(availableSubs);
    }, [getSubscriptionsForAttendanceEntry]);

    // Load existing attendance for the selected date (exact same as MySubscriptions)
    const loadExistingAttendance = useCallback(async (date: string) => {
        if (!userData?.phone_number || !ServiceProviderID) return;

        try {
            const now = new Date(date);
            const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            
            // Use the actual logged-in user's ServiceProvider ID as staffId
            // This ensures each user's attendance is stored under their own ServiceProvider ID
            const staffId = userData?.uid || 'unknown';
            
            const attendanceRef = doc(
                db, 
                'ServiceProviders', 
                ServiceProviderID, 
                'Staff', 
                staffId,
                'Attendance',
                monthKey
            );

            const attendanceDoc = await getDoc(attendanceRef);
            
            if (attendanceDoc.exists()) {
                const dailyEntries = attendanceDoc.data().dailyEntries || [];
                const existingEntry = dailyEntries.find((entry: any) => entry.date === date && entry.status !== 'deleted');
                
                if (existingEntry) {
                    // Pre-populate form with existing data
                    const subscriptions = existingEntry.subscriptions || [];
                    const totalHours = subscriptions.reduce((sum: number, sub: any) => sum + (sub.hours || 0), 0);
                    
                    setAttendanceFormData({
                        date: date,
                        subscriptions: subscriptions,
                        totalHours: totalHours
                    });
                    setIsEditingExistingEntry(true);
                    setCurrentEntryStatus(existingEntry.status || null);
                    return existingEntry;
                }
            }
            
            // No existing entry found, reset form
            setAttendanceFormData({
                date: date,
                subscriptions: [],
                totalHours: 0
            });
            setIsEditingExistingEntry(false);
            setCurrentEntryStatus(null);
            return null;
        } catch (error) {
            console.error('Error loading existing attendance:', error);
            return null;
        }
    }, [userData, ServiceProviderID]);

    // Load subscriptions when modal opens (exact same as MySubscriptions)
    useEffect(() => {
        if (showCheckInModal) {
            const isOwner = !activeRole || activeRole?.role === null;
            
            if ((isOwner && Object.keys(subscriptionsMap).length > 0) || 
                (!isOwner && (acceptedRoles.length > 0 || activeRole))) {
                loadUserSubscriptions();
                loadExistingAttendance(attendanceFormData.date);
            }
        }
    }, [showCheckInModal, loadUserSubscriptions, subscriptionsMap, acceptedRoles, activeRole, loadExistingAttendance, attendanceFormData.date]);

    // Load existing attendance when date changes (exact same as MySubscriptions)
    useEffect(() => {
        if (showCheckInModal && attendanceFormData.date) {
            loadExistingAttendance(attendanceFormData.date);
        }
    }, [attendanceFormData.date, showCheckInModal, loadExistingAttendance]);

    // Handle subscription hours change (exact same as MySubscriptions)
    const handleSubscriptionHoursChange = (subscriptionId: string, hours: number) => {
        const updatedSubscriptions = attendanceFormData.subscriptions.filter(sub => sub.subscriptionId !== subscriptionId);
        
        if (hours > 0) {
            const subscription = availableSubscriptions.find(s => s.id === subscriptionId);
            updatedSubscriptions.push({ 
                subscriptionId, 
                hours, 
                subscriptionName: subscription?.Name || 'Unknown Subscription' 
            });
        }
        
        const totalHours = updatedSubscriptions.reduce((sum, sub) => sum + sub.hours, 0);
        
        setAttendanceFormData({
            ...attendanceFormData,
            subscriptions: updatedSubscriptions,
            totalHours
        });
    };

    // Handle marking as absent (exact same as MySubscriptions)
    const handleMarkAsAbsent = async () => {
        // Check if month is locked
        if (isCurrentMonthLocked()) {
            toast.error('Cannot mark as absent - month is locked');
            return;
        }

        // Clear the form first
        setAttendanceFormData(prev => ({
            ...prev,
            subscriptions: [],
            totalHours: 0
        }));
        
        // Then submit as absent
        await handleCheckInSubmit(true);
    };

    // Get staff ID - use the actual logged-in user's ServiceProvider ID
    // This ensures each user's attendance is stored under their own ServiceProvider ID
    const getStaffId = useCallback(() => {
        return userData?.uid || 'unknown';
    }, [userData?.uid]);


    // Submit attendance entry (exact same as MySubscriptions)
    const handleCheckInSubmit = async (markAsAbsent = false) => {
        if (isCurrentMonthLocked()) {
            toast.error('Cannot submit attendance - month is locked');
            return;
        }

        if (!markAsAbsent && attendanceFormData.totalHours === 0) {
            toast.error('Please add hours for at least one subscription or mark as absent');
            return;
        }

        try {
            const now = new Date(attendanceFormData.date);
            const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            
            // Use the actual logged-in user's ServiceProvider ID as staffId
            // This ensures each user's attendance is stored under their own ServiceProvider ID
            const staffId = userData?.uid || 'unknown';
            
            const attendanceRef = doc(
                db, 
                'ServiceProviders', 
                ServiceProviderID, 
                'Staff', 
                staffId,
                'Attendance', 
                monthKey
            );
            
            const entryStatus = markAsAbsent ? 'absent' : 'present';
            const newEntry = {
                id: Date.now().toString(),
                date: attendanceFormData.date,
                subscriptions: markAsAbsent ? [] : attendanceFormData.subscriptions,
                totalHours: markAsAbsent ? 0 : attendanceFormData.totalHours,
                status: entryStatus,
                createdAt: new Date(),
                createdBy: userData?.display_name || userData?.email || 'Unknown',
                auditLog: [{
                    action: 'created',
                    timestamp: new Date(),
                    user: userData?.display_name || userData?.email || 'Unknown',
                    details: entryStatus === 'absent' 
                        ? 'Explicitly marked as absent via My World check-in' 
                        : `Created attendance entry via My World check-in with ${attendanceFormData.totalHours} hours`
                }]
            };
            
            // Get existing entries for this month
            const existingDoc = await getDoc(attendanceRef);
            const existingEntries = existingDoc.exists() ? (existingDoc.data().dailyEntries || []) : [];
            
            // Check if entry for this date already exists (excluding deleted entries)
            const existingEntryIndex = existingEntries.findIndex((entry: any) => entry.date === attendanceFormData.date && entry.status !== 'deleted');
            
            let updatedEntries;
            let actionMessage;

            if (existingEntryIndex >= 0) {
                // Update existing entry
                const existingEntry = existingEntries[existingEntryIndex];
                const updatedEntry = {
                    ...existingEntry,
                    subscriptions: markAsAbsent ? [] : attendanceFormData.subscriptions,
                    totalHours: markAsAbsent ? 0 : attendanceFormData.totalHours,
                    status: entryStatus,
                    updatedAt: new Date(),
                    updatedBy: userData?.display_name || userData?.email || 'Unknown',
                    auditLog: [
                        ...(existingEntry.auditLog || []),
                        {
                            action: 'updated',
                            timestamp: new Date(),
                            user: userData?.display_name || userData?.email || 'Unknown',
                            details: entryStatus === 'absent' 
                                ? 'Updated to absent via My World check-in' 
                                : `Updated attendance via My World check-in to ${attendanceFormData.totalHours} hours`,
                            previousValues: {
                                status: existingEntry.status,
                                totalHours: existingEntry.totalHours,
                                subscriptions: existingEntry.subscriptions
                            }
                        }
                    ]
                };
                
                updatedEntries = [...existingEntries];
                updatedEntries[existingEntryIndex] = updatedEntry;
                actionMessage = markAsAbsent ? 'Updated to absent successfully' : 'Attendance updated successfully';
            } else {
                // Create new entry
                updatedEntries = [...existingEntries, newEntry];
                actionMessage = markAsAbsent ? 'Marked as absent successfully' : 'Attendance submitted successfully';
            }
            
            await setDoc(attendanceRef, {
                staffId: staffId,
                month: monthKey,
                dailyEntries: updatedEntries,
                lastUpdated: new Date(),
                lastUpdatedBy: userData?.display_name || userData?.email || 'Unknown'
            });

            toast.success(actionMessage);
            setShowCheckInModal(false);
            
            // Reset form
            setAttendanceFormData({
                date: new Date().toISOString().split('T')[0],
                subscriptions: [],
                totalHours: 0
            });

            setIsEditingExistingEntry(false);
            setCurrentEntryStatus(null);

            // Reload attendance data if viewing current month
            if (attendanceYear === now.getFullYear() && attendanceMonth === now.getMonth()) {
                loadMyAttendance();
            }
            
        } catch (error) {
            console.error('Error submitting attendance:', error);
            toast.error('Failed to submit attendance');
        }
    };


    // Load my attendance data (adapted from TeamManagement)
    const loadMyAttendance = useCallback(async () => {
        setAttendanceLoading(true);
        try {
            const monthKey = `${attendanceYear}-${String(attendanceMonth + 1).padStart(2, '0')}`;
            const staffId = getStaffId();
            
            const attendanceRef = doc(
                db, 
                'ServiceProviders', 
                ServiceProviderID, 
                'Staff', 
                staffId,
                'Attendance', 
                monthKey
            );
            
            const attendanceDoc = await getDoc(attendanceRef);
            
            if (attendanceDoc.exists()) {
                const data = attendanceDoc.data();
                const entries = data.dailyEntries || [];
                const validEntries = entries.filter(entry => entry.status !== 'deleted');
                setMyAttendance(validEntries);
            } else {
                setMyAttendance([]);
            }
        } catch (error) {
            console.error('Error loading attendance data:', error);
            toast.error('Failed to load attendance data');
            setMyAttendance([]);
        } finally {
            setAttendanceLoading(false);
        }
    }, [attendanceYear, attendanceMonth, ServiceProviderID, getStaffId]);



    // Get today's attendance
    const getTodaysAttendance = () => {
        const today = new Date().toISOString().split('T')[0];
        return myAttendance.find(entry => entry.date === today);
    };

    // Load locked months from Firestore (same as TeamManagement)
    const loadLockedMonths = useCallback(async () => {
        if (!ServiceProviderID) return;
        
        try {
            const lockRef = doc(db, 'ServiceProviders', ServiceProviderID, 'Settings', 'attendanceLocks');
            const lockDoc = await getDoc(lockRef);
            
            if (lockDoc.exists()) {
                setLockedMonths(lockDoc.data().lockedMonths || {});
            }
        } catch (error) {
            console.error('Error loading locked months:', error);
        }
    }, [ServiceProviderID]);

    // Check if current month is locked (same as TeamManagement)
    const isCurrentMonthLocked = useCallback(() => {
        const monthKey = `${attendanceYear}-${String(attendanceMonth + 1).padStart(2, '0')}`;
        return lockedMonths[monthKey] || false;
    }, [attendanceYear, attendanceMonth, lockedMonths]);

    // Calendar rendering (adapted from TeamManagement)
    const renderCalendar = () => {
        const daysInMonth = new Date(attendanceYear, attendanceMonth + 1, 0).getDate();
        const firstDayOfMonth = new Date(attendanceYear, attendanceMonth, 1).getDay();
        
        const attendanceByDate = myAttendance.reduce((acc, entry) => {
            acc[entry.date] = entry;
            return acc;
        }, {});
        
        const calendarDays = [];
        
        for (let i = 0; i < firstDayOfMonth; i++) {
            calendarDays.push(null);
        }
        
        for (let day = 1; day <= daysInMonth; day++) {
            const dateStr = `${attendanceYear}-${String(attendanceMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const entry = attendanceByDate[dateStr];
            calendarDays.push({
                day,
                date: dateStr,
                entry,
                totalHours: entry?.totalHours || 0,
                subscriptions: entry?.subscriptions || []
            });
        }

        return (
            <div className="space-y-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                    <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {new Date(attendanceYear, attendanceMonth).toLocaleDateString('en-US', { 
                            month: 'long', 
                            year: 'numeric' 
                        })}
                    </Typography>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center w-full sm:w-auto gap-2 sm:space-x-3">
                        <div className="flex items-center space-x-2">
                            <Button 
                                onClick={() => {
                                    if (attendanceMonth === 0) {
                                        setAttendanceMonth(11);
                                        setAttendanceYear(attendanceYear - 1);
                                    } else {
                                        setAttendanceMonth(attendanceMonth - 1);
                                    }
                                }}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '4px 8px', sm: '6px 16px' } }}
                            >
                                Previous
                            </Button>
                            <Button 
                                onClick={() => {
                                    if (attendanceMonth === 11) {
                                        setAttendanceMonth(0);
                                        setAttendanceYear(attendanceYear + 1);
                                    } else {
                                        setAttendanceMonth(attendanceMonth + 1);
                                    }
                                }}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '4px 8px', sm: '6px 16px' } }}
                            >
                                Next
                            </Button>
                        </div>
                        <Button
                            variant="contained"
                            startIcon={<CheckCircleIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
                            onClick={() => setShowCheckInModal(true)}
                            className="bg-blue-600 hover:bg-blue-700 shadow-lg"
                            size="small"
                            disabled={isCurrentMonthLocked()}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '6px 12px', sm: '8px 16px' } }}
                            fullWidth
                        >
                            {isCurrentMonthLocked() ? 'Month Locked' : 'Check In'}
                        </Button>
                    </div>
                </div>

                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-lg">
                    {/* Month Lock Warning */}
                    {isCurrentMonthLocked() && (
                        <div className="bg-red-50 border-b border-red-200 p-3">
                            <div className="flex items-center gap-2">
                                <span className="text-red-600">🔒</span>
                                <p className="text-sm text-red-800 font-medium">
                                    <strong>Month Locked:</strong> Attendance cannot be modified for this month.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Calendar Header */}
                    <div className="grid grid-cols-7 bg-gradient-to-r from-slate-100 via-gray-100 to-slate-100 border-b border-gray-300">
                        {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
                            <div key={day} className="p-2 sm:p-4 text-center text-xs sm:text-sm font-bold text-gray-700 border-r border-gray-300 last:border-r-0">
                                <div className="hidden sm:block">{day}</div>
                                <div className="sm:hidden">{day.substr(0, 3)}</div>
                            </div>
                        ))}
                    </div>

                    {/* Calendar Body */}
                    <div className="grid grid-cols-7">
                        {calendarDays.map((dayData, index) => {
                            if (!dayData) {
                                return (
                                    <div key={index} className="h-16 sm:h-24 md:h-28 border-r border-b border-gray-200 bg-gradient-to-br from-gray-50 to-slate-100 last:border-r-0"></div>
                                );
                            }

                            const { day, entry, totalHours, subscriptions } = dayData;
                            const isPresent = entry && entry.status === 'present' && totalHours > 0;
                            const isExplicitAbsent = entry && entry.status === 'absent';
                            const isToday = new Date().toDateString() === new Date(attendanceYear, attendanceMonth, day).toDateString();

                            return (
                                <div
                                    key={day}
                                    className={`h-16 sm:h-24 md:h-28 border-r border-b border-gray-300 last:border-r-0 relative transition-all duration-300 group cursor-pointer hover:shadow-lg hover:scale-105 transform ${
                                        isToday ? 'ring-2 ring-blue-500 ring-inset shadow-md' : ''
                                    } ${
                                        isPresent 
                                            ? 'bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 hover:from-emerald-100 hover:via-teal-100 hover:to-cyan-100 border-emerald-300 shadow-sm'
                                            : isExplicitAbsent
                                                ? 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 hover:from-orange-100 hover:via-amber-100 hover:to-yellow-100 border-orange-300 shadow-sm'
                                                : 'bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 hover:from-slate-100 hover:via-gray-100 hover:to-zinc-100 border-slate-300'
                                    }`}
           onClick={() => {
               // Check if month is locked
               if (isCurrentMonthLocked()) {
                   toast.error('Month is locked - no modifications allowed');
                   return;
               }
               
               // Set the date in form data and open modal
               setAttendanceFormData(prev => ({
                   ...prev,
                   date: dayData.date
               }));
               setShowCheckInModal(true);
           }}
                                >
                                    {/* Day number */}
                                    <div className={`absolute top-1 sm:top-2 left-1 sm:left-2 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                        isToday 
                                            ? 'bg-blue-500 text-white shadow-md' 
                                            : isPresent 
                                                ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                                                : isExplicitAbsent
                                                    ? 'bg-orange-100 text-orange-700 border border-orange-300'
                                                    : 'bg-gray-100 text-gray-600 border border-gray-300'
                                    }`}>
                                        {day}
                                    </div>
                                    
                                    {/* Hours display */}
                                    <div className="absolute bottom-1 sm:bottom-2 left-1 sm:left-2 right-1 sm:right-2">
                                        {isPresent ? (
                                            <div className="text-center bg-white bg-opacity-90 rounded-lg py-0.5 sm:py-1 px-1 sm:px-2 backdrop-blur-sm">
                                                <div className="text-xs sm:text-sm font-bold text-emerald-700">
                                                    {totalHours}h
                                                </div>
                                                {subscriptions.length > 1 && (
                                                    <div className="text-[10px] sm:text-xs text-emerald-600">
                                                        {subscriptions.length} subs
                                                    </div>
                                                )}
                                            </div>
                                        ) : isExplicitAbsent ? (
                                            <div className="text-center bg-orange-100 bg-opacity-90 rounded-lg py-0.5 sm:py-1 px-1 sm:px-2 backdrop-blur-sm">
                                                <div className="text-[10px] sm:text-xs text-orange-700 font-medium">
                                                    Absent
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center bg-slate-100 bg-opacity-90 rounded-lg py-0.5 sm:py-1 px-1 sm:px-2 backdrop-blur-sm">
                                                <div className="text-[10px] sm:text-xs text-slate-600 font-medium">
                                                    No record
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    useEffect(() => {
        loadMyAttendance();
    }, [loadMyAttendance]);

    // Load locked months when component mounts
    useEffect(() => {
        if (ServiceProviderID) {
            loadLockedMonths();
        }
    }, [ServiceProviderID, loadLockedMonths]);

    const todaysAttendance = getTodaysAttendance();

    return (
        <div className="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">My World</h1>
                <p className="text-sm sm:text-base text-gray-600">Personal attendance tracking and calendar view</p>
            </div>

            {/* Today's Status Card */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                        <div className="flex items-center space-x-2 sm:space-x-4">
                            <div className="p-2 sm:p-3 bg-blue-100 rounded-full">
                                <CalendarTodayIcon className="text-blue-600" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }} />
                            </div>
                            <div>
                                <Typography variant="h6" className="text-gray-900" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    Today's Status
                                </Typography>
                                {todaysAttendance ? (
                                    <div className="flex items-center space-x-2 mt-1">
                                        <Chip 
                                            icon={<CheckCircleIcon />}
                                            label={todaysAttendance.status === 'present' ? `${todaysAttendance.totalHours}h logged` : 'Marked Absent'}
                                            color={todaysAttendance.status === 'present' ? 'success' : 'warning'}
                                            size="small"
                                        />
                                        {todaysAttendance.status === 'present' && (
                                            <Typography variant="body2" className="text-gray-600">
                                                {todaysAttendance.subscriptions.length} subscription(s)
                                            </Typography>
                                        )}
                                    </div>
                                ) : (
                                    <Typography variant="body2" className="text-gray-600 mt-1">
                                        No attendance recorded for today
                                    </Typography>
                                )}
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Side-by-side Layout */}
            {attendanceLoading ? (
                <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                    {/* Calendar - Takes 2/3 of the width */}
                    <div className="lg:col-span-2">
                        {renderCalendar()}
                    </div>

                    {/* Stats Sidebar - Takes 1/3 of the width */}
                    <div className="space-y-3 sm:space-y-4">
                        {/* Monthly Summary Stats */}
                        <div className="bg-gradient-to-r from-slate-50 to-blue-50 p-3 sm:p-4 rounded-lg border border-slate-200">
                            <Typography variant="h6" className="mb-2 sm:mb-3 text-gray-800 text-center" sx={{ fontSize: { xs: '0.875rem', sm: '1.25rem' } }}>
                                Monthly Summary
                            </Typography>
                            <div className="grid grid-cols-2 gap-2 sm:gap-3">
                                <div className="text-center p-2 sm:p-3 bg-white rounded-lg shadow-sm">
                                    <div className="text-base sm:text-xl font-bold text-emerald-600">
                                        {myAttendance.filter(entry => entry.totalHours > 0).length}
                                    </div>
                                    <div className="text-[10px] sm:text-xs text-gray-600 font-medium">Present</div>
                                </div>
                                <div className="text-center p-2 sm:p-3 bg-white rounded-lg shadow-sm">
                                    <div className="text-base sm:text-xl font-bold text-slate-600">
                                        {myAttendance.filter(entry => entry.status === 'absent').length}
                                    </div>
                                    <div className="text-[10px] sm:text-xs text-gray-600 font-medium">Absent</div>
                                </div>
                                <div className="text-center p-2 sm:p-3 bg-white rounded-lg shadow-sm">
                                    <div className="text-base sm:text-xl font-bold text-blue-600">
                                        {myAttendance.reduce((sum, entry) => sum + (entry.totalHours || 0), 0)}h
                                    </div>
                                    <div className="text-[10px] sm:text-xs text-gray-600 font-medium">Total Hours</div>
                                </div>
                                <div className="text-center p-2 sm:p-3 bg-white rounded-lg shadow-sm">
                                    <div className="text-base sm:text-xl font-bold text-indigo-600">
                                        {myAttendance.filter(entry => entry.totalHours > 0).length > 0 
                                            ? (myAttendance.reduce((sum, entry) => sum + (entry.totalHours || 0), 0) / myAttendance.filter(entry => entry.totalHours > 0).length).toFixed(1)
                                            : '0'
                                        }h
                                    </div>
                                    <div className="text-[10px] sm:text-xs text-gray-600 font-medium">Avg/Day</div>
                                </div>
                            </div>
                        </div>

                        {/* Subscription Breakdown */}
                        <Card>
                            <CardContent className="p-3 sm:p-4">
                                <Typography variant="h6" className="mb-2 sm:mb-3 text-gray-800" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    Hours by Subscription
                                </Typography>
                                {(() => {
                                    const subscriptionHours = myAttendance.reduce((acc, entry) => {
                                        entry.subscriptions?.forEach((sub: any) => {
                                            if (!acc[sub.subscriptionName]) {
                                                acc[sub.subscriptionName] = 0;
                                            }
                                            acc[sub.subscriptionName] += sub.hours || 0;
                                        });
                                        return acc;
                                    }, {} as Record<string, number>);

                                    const sortedSubscriptions = Object.entries(subscriptionHours)
                                        .sort(([,a], [,b]) => (b as number) - (a as number));

                                    return sortedSubscriptions.length > 0 ? (
                                        <div className="space-y-2">
                                            {sortedSubscriptions.slice(0, 5).map(([name, hours]) => (
                                                <div key={name} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                                                    <span className="font-medium text-gray-700 truncate mr-2">{name}</span>
                                                    <span className="text-blue-600 font-bold">{hours as number}h</span>
                                                </div>
                                            ))}
                                            {sortedSubscriptions.length > 5 && (
                                                <div className="text-xs text-gray-500 text-center">
                                                    +{sortedSubscriptions.length - 5} more
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4 text-gray-500">
                                            <div className="text-2xl mb-1">📊</div>
                                            <p className="text-xs">No hours logged</p>
                                        </div>
                                    );
                                })()}
                            </CardContent>
                        </Card>

                        {/* Weekly Attendance Summary */}
                        <Card>
                            <CardContent className="p-3 sm:p-4">
                                <Typography variant="h6" className="mb-2 sm:mb-3 text-gray-800" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                    Weekly Summary
                                </Typography>
                                {(() => {
                                    const currentDate = new Date();
                                    const currentWeekStart = new Date(currentDate);
                                    currentWeekStart.setDate(currentDate.getDate() - currentDate.getDay());
                                    currentWeekStart.setHours(0, 0, 0, 0);
                                    
                                    const currentWeekEnd = new Date(currentWeekStart);
                                    currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
                                    currentWeekEnd.setHours(23, 59, 59, 999);

                                    const weekEntries = myAttendance.filter(entry => {
                                        const entryDate = new Date(entry.date);
                                        return entryDate >= currentWeekStart && entryDate <= currentWeekEnd;
                                    });

                                    const weekStats = {
                                        presentDays: weekEntries.filter(entry => entry.totalHours > 0).length,
                                        absentDays: weekEntries.filter(entry => entry.status === 'absent').length,
                                        totalHours: weekEntries.reduce((sum, entry) => sum + (entry.totalHours || 0), 0),
                                        workingDays: 7 - (currentDate.getDay() === 0 ? 1 : 0) // Exclude Sunday if today is Sunday
                                    };

                                    return (
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-2 gap-2">
                                                <div className="text-center p-2 bg-emerald-50 rounded">
                                                    <div className="text-lg font-bold text-emerald-600">{weekStats.presentDays}</div>
                                                    <div className="text-xs text-gray-600">Present</div>
                                                </div>
                                                <div className="text-center p-2 bg-orange-50 rounded">
                                                    <div className="text-lg font-bold text-orange-600">{weekStats.absentDays}</div>
                                                    <div className="text-xs text-gray-600">Absent</div>
                                                </div>
                                            </div>
                                            <div className="text-center p-2 bg-blue-50 rounded">
                                                <div className="text-lg font-bold text-blue-600">{weekStats.totalHours}h</div>
                                                <div className="text-xs text-gray-600">This Week</div>
                                            </div>
                                            <div className="text-center p-2 bg-gray-50 rounded">
                                                <div className="text-sm font-medium text-gray-700">
                                                    {currentWeekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {currentWeekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                </div>
                                                <div className="text-xs text-gray-500">Week Range</div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            )}

            {/* Check-in Modal (exact same as MySubscriptions) */}
            <Dialog 
                open={showCheckInModal} 
                onClose={() => {
                    setShowCheckInModal(false);
                    setIsEditingExistingEntry(false);
                    setCurrentEntryStatus(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className="flex items-center justify-between">
                    <div>
                        <span className="text-lg font-semibold">
                            {isEditingExistingEntry ? 'Edit Attendance' : 'Staff Check-in'}
                        </span>
                        {isEditingExistingEntry && (
                            <div className="text-sm text-gray-600 mt-1">
                                Updating existing attendance for {new Date(attendanceFormData.date).toLocaleDateString()}
                            </div>
                        )}
                    </div>
                    <IconButton onClick={() => {
                        setShowCheckInModal(false);
                        setIsEditingExistingEntry(false);
                        setCurrentEntryStatus(null);
                    }} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <div className="space-y-4 pt-2">
                        {/* Lock Status Warning */}
                        {isCurrentMonthLocked() && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                <p className="text-sm text-red-800">
                                    <strong>Month Locked:</strong> Attendance cannot be submitted for this month.
                                </p>
                            </div>
                        )}

                        {/* Current Entry Status */}
                        {currentEntryStatus === 'absent' && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                                <p className="text-sm text-orange-800">
                                    <strong>Currently Marked as Absent:</strong> This date is marked as absent. You can update it by changing hours or marking absent again.
                                </p>
                            </div>
                        )}

                        {/* Date Selection */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                            <TextField
                                type="date"
                                value={attendanceFormData.date}
                                onChange={(e) => setAttendanceFormData({ ...attendanceFormData, date: e.target.value })}
                                fullWidth
                                size="small"
                                disabled={isCurrentMonthLocked()}
                            />
                        </div>

                        {/* Available Subscriptions */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Hours by Subscription</label>
                            {availableSubscriptions.length === 0 ? (
                                <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                                    <div className="text-3xl mb-2">🔒</div>
                                    <p className="font-medium">No subscriptions available</p>
                                    <p className="text-xs mt-1">
                                        {(!activeRole || activeRole?.role === null)
                                            ? `Loading your subscriptions... (${Object.keys(subscriptionsMap).length} total found)` 
                                            : "You may not have any assigned subscriptions"}
                                    </p>
                                    <p className="text-xs mt-1 text-gray-400">
                                        Role: {activeRole?.role || 'Owner'} | Subscriptions loaded: {Object.keys(subscriptionsMap).length} | Roles loaded: {acceptedRoles.length}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 bg-gray-50">
                                    {availableSubscriptions.map((subscription) => {
                                        const existingHours = attendanceFormData.subscriptions.find(sub => sub.subscriptionId === subscription.id)?.hours || 0;
                                        return (
                                            <div key={subscription.id} className="flex items-center justify-between p-3 bg-white rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                                                <div className="flex-1">
                                                    <h4 className="font-medium text-sm text-gray-900">{subscription.Name}</h4>
                                                    {subscription.Description && (
                                                        <p className="text-xs text-gray-500 mt-1">{subscription.Description}</p>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <TextField
                                                        type="number"
                                                        value={existingHours}
                                                        onChange={(e) => handleSubscriptionHoursChange(subscription.id, parseFloat(e.target.value) || 0)}
                                                        placeholder="0"
                                                        size="small"
                                                        disabled={isCurrentMonthLocked()}
                                                        inputProps={{ 
                                                            min: 0, 
                                                            max: 24, 
                                                            step: 0.5,
                                                            style: { width: '70px', textAlign: 'center' }
                                                        }}
                                                    />
                                                    <span className="text-xs text-gray-500 font-medium">hrs</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Total Hours Display */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-sm font-medium text-blue-800">
                                Total Hours: {attendanceFormData.totalHours}h
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-4">
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setShowCheckInModal(false);
                                setIsEditingExistingEntry(false);
                                setCurrentEntryStatus(null);
                            }}
                            fullWidth
                            size="small"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '6px 12px', sm: '8px 16px' } }}
                        >
                            Cancel
                        </Button>
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={handleMarkAsAbsent}
                                disabled={isCurrentMonthLocked()}
                                fullWidth
                                size="small"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '6px 12px', sm: '8px 16px' } }}
                            >
                                Mark as Absent
                            </Button>
                            <Button
                                variant="contained"
                                onClick={() => handleCheckInSubmit(false)}
                                disabled={(attendanceFormData.totalHours === 0 && currentEntryStatus !== 'absent') || isCurrentMonthLocked() || availableSubscriptions.length === 0}
                                fullWidth
                                size="small"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '6px 12px', sm: '8px 16px' } }}
                            >
                                {isEditingExistingEntry ? 'Update Attendance' : 'Submit Check-in'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}